import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    evaluateTemplateValueCollection,
    GlobalDataContext,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { saveDocDirectory } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import DocAccessPanel from './DocAccessPanel';
import { unwrap } from '../../../utils/decoupled';
import {
    Briefcase,
    Check,
    Loader2,
    Search,
    Settings,
    ShieldCheck,
    User,
    X,
} from 'lucide-react';

const CATEGORIES_URL = '/decoupled-api/taxonomy_term/doc_space_category/collection?display=decoupled';
const USERS_URL = '/decoupled-api/user/user/collection?display=decoupled';

// Plus d'onglet « Catégories » : affilier un espace, c'est une PROPRIÉTÉ de
// l'espace, au même titre que son nom ou son projet — elle vit donc dans
// « Général », en une liste déroulante. Le vocabulaire lui-même (créer,
// renommer, hiérarchiser les catégories) se gère à sa place, dans la
// configuration de la doc (/docs/settings, onglet « Catégories »).
const TABS = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'access', label: 'Accès', icon: ShieldCheck },
];

function normalizeCategory(item) {
    const d = item.data ?? item;
    const rawParent = d.parentId;
    const parentId = Array.isArray(rawParent)
        ? (rawParent[0] ? parseInt(rawParent[0], 10) : 0)
        : (rawParent ? parseInt(rawParent, 10) : 0);
    return { id: d.id, name: d.name, parentId: parentId || 0, weight: d.weight || 0, description: d.description || '' };
}

export const DocSpaceSettings = ({ props }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    const { user, isAdmin } = useAuth();

    const ev = (val) => evaluateTemplateValue({ valueToEvaluate: val, templateContext, globalDataContext });
    const evCol = (val) => evaluateTemplateValueCollection({ valueToEvaluate: val, templateContext, globalDataContext });

    const isOpen = ev(props.isOpen);
    const openPath = props.openPath || 'showSpaceSettings';
    const spaceId = ev(props.directoryId);

    const spaceName = ev(props.spaceName) || '';
    const spaceDescription = ev(props.spaceDescription) || '';
    const spaceProjectId = ev(props.spaceProjectId);
    const rawCategoryIds = ev(props.spaceCategoryIds);
    const spaceCategoryIds = useMemo(
        () => (Array.isArray(rawCategoryIds) ? rawCategoryIds.map(Number).filter(Number.isFinite) : []),
        [rawCategoryIds],
    );
    const projects = useMemo(() => {
        const raw = evCol(props.projects) || [];
        return raw.map((item) => {
            const d = item.data ?? item;
            return { id: d.id, name: d.name || `Projet #${d.id}` };
        });
    }, [evCol, props.projects]);

    const [tab, setTab] = useState('general');

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const [projectId, setProjectId] = useState('');
    const [savingGeneral, setSavingGeneral] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const [generalSuccess, setGeneralSuccess] = useState(false);

    const [linkedProjectName, setLinkedProjectName] = useState('');

    const [ownerId, setOwnerId] = useState(0);
    const [originalOwnerId, setOriginalOwnerId] = useState(0);
    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerName, setOwnerName] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [usersLoaded, setUsersLoaded] = useState(false);
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

    const [allCategories, setAllCategories] = useState([]);
    const [loadingCats, setLoadingCats] = useState(false);
    const [catsLoaded, setCatsLoaded] = useState(false);
    const [localCatIds, setLocalCatIds] = useState([]);

    useEffect(() => {
        if (!isOpen) return;
        setName(spaceName);
        setDescription(spaceDescription);

        setProjectId(spaceProjectId ? String(spaceProjectId) : '');
        setLocalCatIds([...spaceCategoryIds]);
        setOwnerSearch('');
        setOwnerName('');
        setShowOwnerDropdown(false);
        setGeneralError('');
        setGeneralSuccess(false);
        setTab('general');
        setCatsLoaded(false);
        setUsersLoaded(false);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !spaceId || usersLoaded) return;
        let cancelled = false;
        Promise.all([
            fetch(USERS_URL, { credentials: 'include' }).then((r) => r.ok ? r.json() : []),
            fetch(`/decoupled-api/taxonomy_term/doc_directory/${spaceId}?display=decoupled`, { credentials: 'include' })
                .then((r) => r.ok ? r.json() : null),
        ]).then(async ([usersData, spaceData]) => {
            if (cancelled) return;
            const users = (Array.isArray(usersData) ? usersData : [])
                .filter((item) => item?.data ?? item?.id != null)
                .map((item) => {
                    const d = item.data ?? item;
                    return { id: typeof d.id === 'string' ? parseInt(d.id, 10) : d.id, name: d.name || '' };
                });
            setAllUsers(users);

            const apiOwnerId = Number(spaceData?.data?.ownerId) || 0;
            setOwnerId(apiOwnerId);
            setOriginalOwnerId(apiOwnerId);

            if (apiOwnerId) {
                const match = users.find((u) => u.id === apiOwnerId);
                if (match) setOwnerName(match.name);
            }

            const apiProjectId = spaceData?.data?.projectId;
            if (apiProjectId && !cancelled) {
                setProjectId(String(apiProjectId));
                try {
                    const projRes = await fetch(`/decoupled-api/group/project/${apiProjectId}?display=decoupled`, { credentials: 'include' });
                    if (projRes.ok && !cancelled) {
                        const projData = unwrap(await projRes.json());
                        setLinkedProjectName(projData?.name || `Projet #${apiProjectId}`);
                    } else if (!cancelled) {
                        setLinkedProjectName(`Projet #${apiProjectId}`);
                    }
                } catch {
                    if (!cancelled) setLinkedProjectName(`Projet #${apiProjectId}`);
                }
            } else {
                setLinkedProjectName('');
            }

            setUsersLoaded(true);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [isOpen, spaceId, usersLoaded]);

    const filteredOwnerUsers = useMemo(() => {
        if (!ownerSearch.trim()) return [];
        const q = ownerSearch.toLowerCase();
        return allUsers.filter((u) => u.name.toLowerCase().includes(q)).slice(0, 8);
    }, [ownerSearch, allUsers]);

    const canChangeOwner = isAdmin || (originalOwnerId > 0 && Number(user?.uid) === originalOwnerId);

    // Le vocabulaire est chargé dès l'ouverture : la liste déroulante des
    // catégories vit maintenant dans « Général », l'onglet par défaut.
    useEffect(() => {
        if (!isOpen || catsLoaded || loadingCats) return;
        setLoadingCats(true);
        fetch(CATEGORIES_URL, { credentials: 'include' })
            .then((r) => { if (!r.ok) throw new Error(`${r.status}`); return r.json(); })
            .then((data) => {
                const cats = (Array.isArray(data) ? data : [])
                    .filter((item) => item?.data ?? item?.id != null)
                    .map(normalizeCategory);
                setAllCategories(cats);
                setCatsLoaded(true);
            })
            .catch(() => setAllCategories([]))
            .finally(() => setLoadingCats(false));
    }, [isOpen, catsLoaded, loadingCats]);

    const handleClose = useCallback(() => {
        globalDataContext.updateData(false, openPath);
    }, [globalDataContext, openPath]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, handleClose]);

    const handleSaveGeneral = useCallback(async () => {
        const trimmed = name.trim();
        if (!trimmed) { setGeneralError('Le nom est obligatoire.'); return; }
        setSavingGeneral(true);
        setGeneralError('');
        setGeneralSuccess(false);
        try {
            // Les catégories partent avec le reste : elles sont un champ de
            // l'espace comme les autres depuis qu'elles ont quitté leur onglet.
            const payload = { id: spaceId, name: trimmed, description: description.trim(), spaceCategoryIds: localCatIds };
            const pid = projectId ? parseInt(projectId, 10) : 0;
            payload.projectId = pid;
            if (canChangeOwner && ownerId > 0) {
                payload.ownerId = ownerId;
            }
            await saveDocDirectory(payload);
            globalDataContext.updateData(trimmed, 'space.name');
            globalDataContext.updateData(description.trim(), 'space.description');
            globalDataContext.updateData(pid || '', 'space.projectId');
            globalDataContext.updateData(localCatIds, 'space.spaceCategoryIds');
            if (canChangeOwner && ownerId > 0) {
                globalDataContext.updateData(ownerId, 'space.ownerId');
                setOriginalOwnerId(ownerId);
            }
            setGeneralSuccess(true);
            setTimeout(() => setGeneralSuccess(false), 2000);
        } catch (err) {
            setGeneralError(err.message || 'Erreur lors de la sauvegarde.');
        } finally {
            setSavingGeneral(false);
        }
    }, [spaceId, name, description, projectId, canChangeOwner, ownerId, localCatIds, globalDataContext]);

    const rootCategories = useMemo(
        () => allCategories.filter((c) => !c.parentId || c.parentId === 0),
        [allCategories],
    );

    const groupedCategories = useMemo(() => {
        return rootCategories.map((root) => ({
            root,
            children: allCategories.filter((c) => c.parentId === root.id),
        }));
    }, [rootCategories, allCategories]);

    const toggleCategory = useCallback((catId) => {
        setLocalCatIds((prev) => {
            const cat = allCategories.find((c) => c.id === catId);
            if (!cat) return prev;
            const wasSelected = prev.includes(catId);
            if (wasSelected) {
                let next = prev.filter((id) => id !== catId);
                if (!cat.parentId || cat.parentId === 0) {
                    const childIds = allCategories.filter((c) => c.parentId === catId).map((c) => c.id);
                    next = next.filter((id) => !childIds.includes(id));
                }
                return next;
            }
            let next = [...prev, catId];
            if (cat.parentId && cat.parentId !== 0 && !next.includes(cat.parentId)) {
                next = [...next, cat.parentId];
            }
            return next;
        });
    }, [allCategories]);


    const handleAccessSaved = useCallback(() => {}, []);

    if (!isOpen) return null;

    return (
        <ActionDependant {...props}>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClose} />
                <div id="doc-space-settings" className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl border border-stone-200 max-h-[85vh] flex flex-col">

                    <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 flex-shrink-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <Settings size={18} className="text-stone-500 flex-shrink-0" />
                            <h2 className="text-lg font-semibold text-stone-900 truncate">
                                Paramètres — {spaceName || '…'}
                            </h2>
                        </div>
                        <button onClick={handleClose} className="p-1 rounded-lg hover:bg-stone-100 text-stone-400 transition-colors" aria-label="Fermer">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="flex border-b border-stone-200 px-6 flex-shrink-0 overflow-x-auto">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = tab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setTab(t.id)}
                                    className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                        active
                                            ? 'border-stone-900 text-stone-900'
                                            : 'border-transparent text-stone-400 hover:text-stone-600'
                                    }`}
                                >
                                    <Icon size={14} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-5">

                        {tab === 'general' && (
                            <div id="doc-space-settings-general" className="space-y-5">
                                <div>
                                    <label className="field-label mb-1.5">Nom de l'espace</label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="input"
                                        maxLength={128}
                                    />
                                </div>
                                <div>
                                    <label className="field-label mb-1.5">Description</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                        className="textarea"
                                        placeholder="Description de l'espace…"
                                    />
                                </div>
                                <div>
                                    <label className="field-label mb-1.5">
                                        <span className="inline-flex items-center gap-1.5"><Briefcase size={13} /> Projet lié</span>
                                    </label>
                                    {isAdmin ? (
                                        <>
                                            <select
                                                value={projectId}
                                                onChange={(e) => setProjectId(e.target.value)}
                                                className="select"
                                            >
                                                <option value="">Aucun projet</option>
                                                {projects.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                            <p className="text-xs text-stone-400 mt-1">
                                                Associe cet espace à un projet pour accéder à ses tickets.
                                            </p>
                                        </>
                                    ) : (
                                        <div className="px-4 py-2.5 border border-stone-200 rounded-xl bg-stone-50 text-sm text-stone-700">
                                            {linkedProjectName || 'Aucun projet'}
                                        </div>
                                    )}
                                </div>

                                {canChangeOwner && (
                                    <div>
                                        <label className="field-label mb-1.5">
                                            <span className="inline-flex items-center gap-1.5"><User size={13} /> Propriétaire</span>
                                        </label>
                                        <div className="relative">
                                            {ownerName && !showOwnerDropdown ? (
                                                <div className="flex items-center justify-between px-4 py-2.5 border border-stone-200 rounded-xl bg-white">
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                                                            {ownerName.charAt(0).toUpperCase()}
                                                        </div>
                                                        <span className="text-sm text-stone-800 font-medium">{ownerName}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setShowOwnerDropdown(true); setOwnerSearch(''); }}
                                                        className="btn btn-ghost btn-sm"
                                                    >
                                                        Modifier
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="relative">
                                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                                                        <input
                                                            type="text"
                                                            value={ownerSearch}
                                                            onChange={(e) => { setOwnerSearch(e.target.value); setShowOwnerDropdown(true); }}
                                                            onFocus={() => setShowOwnerDropdown(true)}
                                                            onBlur={() => setTimeout(() => setShowOwnerDropdown(false), 200)}
                                                            placeholder="Rechercher un utilisateur…"
                                                            className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                                                        />
                                                    </div>
                                                    {showOwnerDropdown && ownerSearch.trim() && (
                                                        <div className="absolute z-20 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                                            {filteredOwnerUsers.length > 0 ? filteredOwnerUsers.map((u) => (
                                                                <button
                                                                    key={u.id}
                                                                    type="button"
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        setOwnerId(u.id);
                                                                        setOwnerName(u.name);
                                                                        setOwnerSearch('');
                                                                        setShowOwnerDropdown(false);
                                                                    }}
                                                                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-stone-50 transition-colors text-left"
                                                                >
                                                                    <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 text-xs font-bold flex-shrink-0">
                                                                        {u.name.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <span className="text-stone-800">{u.name}</span>
                                                                </button>
                                                            )) : (
                                                                <p className="px-4 py-3 text-xs text-stone-400 italic">Aucun utilisateur trouvé</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <p className="text-xs text-stone-400 mt-1">
                                            {isAdmin ? "Modifiable par l'administrateur." : "Vous pouvez transférer cet espace à un autre utilisateur."}
                                        </p>
                                    </div>
                                )}

                                {/* Catégories — une liste déroulante qui AJOUTE, et
                                    des pastilles qui retirent. Le `select` ne
                                    propose que ce qui n'est pas déjà affilié : il
                                    ne porte donc pas d'état, seulement un geste,
                                    et sa valeur repart à vide après chaque choix.
                                    Les sous-catégories sont groupées sous leur
                                    racine (`optgroup`), et en choisir une affilie
                                    aussi le parent — la même règle que les
                                    pastilles d'avant, portée par `toggleCategory`. */}
                                <div>
                                    <label htmlFor="doc-space-category-select" className="field-label mb-1.5">Catégories</label>
                                    {loadingCats ? (
                                        <p className="flex items-center gap-2 text-sm text-stone-400">
                                            <Loader2 size={14} className="animate-spin" /> Chargement…
                                        </p>
                                    ) : (
                                        <>
                                            {localCatIds.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 mb-2">
                                                    {localCatIds.map((id) => {
                                                        const cat = allCategories.find((c) => c.id === id);
                                                        return (
                                                            <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border bg-emerald-50 border-emerald-300 text-emerald-700 font-medium">
                                                                {cat ? cat.name : `#${id}`}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleCategory(id)}
                                                                    className="text-emerald-600 hover:text-emerald-900"
                                                                    aria-label={`Retirer la catégorie ${cat ? cat.name : id}`}
                                                                >
                                                                    <X size={11} aria-hidden="true" />
                                                                </button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            <select
                                                id="doc-space-category-select"
                                                value=""
                                                onChange={(e) => { if (e.target.value) toggleCategory(Number(e.target.value)); }}
                                                className="select w-full"
                                            >
                                                <option value="">Ajouter une catégorie…</option>
                                                {groupedCategories.map(({ root, children }) => (
                                                    children.length > 0 ? (
                                                        <optgroup key={root.id} label={root.name}>
                                                            {!localCatIds.includes(root.id) && (
                                                                <option value={root.id}>{root.name}</option>
                                                            )}
                                                            {children.filter((c) => !localCatIds.includes(c.id)).map((c) => (
                                                                <option key={c.id} value={c.id}>{c.name}</option>
                                                            ))}
                                                        </optgroup>
                                                    ) : !localCatIds.includes(root.id) ? (
                                                        <option key={root.id} value={root.id}>{root.name}</option>
                                                    ) : null
                                                ))}
                                            </select>
                                            {allCategories.length === 0 && (
                                                <p className="text-xs text-stone-400 mt-1">
                                                    Aucune catégorie n’existe encore — elles se créent dans la configuration de la documentation.
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>

                                {generalError && (
                                    <p className="alert alert-danger">{generalError}</p>
                                )}

                                <div className="flex items-center justify-end gap-3 pt-2">
                                    {generalSuccess && (
                                        <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                                            <Check size={14} /> Enregistré
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleSaveGeneral}
                                        disabled={savingGeneral}
                                        className="btn btn-primary"
                                    >
                                        {savingGeneral && <Loader2 size={14} className="animate-spin" />}
                                        Enregistrer
                                    </button>
                                </div>
                            </div>
                        )}

                        {tab === 'access' && (
                            <div id="doc-space-settings-access">
                                <DocAccessPanel
                                    directoryId={spaceId}
                                    isActive={tab === 'access'}
                                    onSaved={handleAccessSaved}
                                    readOnly={!isAdmin}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ActionDependant>
    );
};
